import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Clock, Gauge, Lock, Globe, Zap, ListChecks, Send, Hourglass, Ban } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { OfferThumb } from "@/components/offers/OfferThumb";
import { StreamDrawer } from "@/drawers/StreamDrawer";
import { http } from "@/lib/api";
import { fmtMoney, flagEmoji, geoName } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { GeoBadges } from "@/components/users/GeoOverridesDialog";

// One shared public info card for a single GEO offer — used by Admin Preview AND Buyer View.
// `card` provides product-level info; `offer` is the GEO child (from the tree, no fetch needed).
export function OfferInfoCard({ card, offer, open, onOpenChange, canCreateStream = false }) {
  const [stream, setStream] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isBuyer = user?.role === "buyer";
  const description = offer?.description || card?.product_description || card?.description;
  // Buyer access state comes from the backend enrich: open | granted | request_required | pending | rejected.
  const access = offer?.access_state;
  // Multi-GEO: the per-GEO payouts block replaces the single "Your/Public payout" figure.
  const multiGeo = !!offer && ((Array.isArray(offer.geos) && offer.geos.length > 1) || (offer.geo_payouts || []).length > 1);
  // Staff have no per-offer access gate — they can always create a stream on an active offer.
  const canStreamNow = !isBuyer || access === "open" || access === "granted";

  const requestAccess = async () => {
    setBusy(true);
    try {
      await http.post("/offer-requests", { offer_id: offer.id });
      qc.invalidateQueries({ queryKey: ["offer-cards"] });
      qc.invalidateQueries({ queryKey: ["offer-requests"] });
      toast.success("Access requested", { description: "Your manager will review it shortly." });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[88vh] overflow-y-auto border-white/[0.1] bg-[#141417] sm:max-w-xl" data-testid="offer-info-card">
          {card && offer && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <OfferThumb image={card.product_image} name={card.product_name} size="lg" />
                  <div className="min-w-0 flex-1">
                    <DialogTitle className="truncate text-lg text-foreground" data-testid="info-product-name">{card.product_name}</DialogTitle>
                    {/* Advertiser / network identity is staff-only — never shown to buyers. */}
                    {!isBuyer && <p className="text-sm text-muted-foreground">{card.advertiser_name} · {card.vertical || "—"}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground"><GeoBadges geo={offer.geo} geos={offer.geos} max={5} testId="info-geo-badges" /></span>
                      <StatusBadge status={offer.status} />
                      {isBuyer && access === "pending" && <StatusBadge status="pending" />}
                      {isBuyer && access === "rejected" && <StatusBadge status="rejected" />}
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex flex-wrap items-center gap-4 rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3">
                {!multiGeo && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground/60">{isBuyer ? "Your payout" : "Public payout"}</div>
                  {isBuyer && offer.payout_is_personal ? (
                    <div className="flex items-baseline gap-2" data-testid="info-payout">
                      <span className="font-mono text-sm text-muted-foreground/50 line-through" data-testid="info-payout-default">{offer.payment_model === "RevShare" ? `${Number(offer.payout_default) || 0}%` : fmtMoney(offer.payout_default)}</span>
                      <span className="font-mono text-base font-semibold text-[#22C55E]" data-testid="info-payout-value">{offer.payment_model === "RevShare" ? `${Number(offer.buyer_payout) || 0}%` : fmtMoney(offer.buyer_payout)}</span>
                      {offer.payment_model === "Hybrid" && (
                        <span className="font-mono text-base font-semibold text-[#F59E0B]" data-testid="info-payout-rs">· RevShare {Number(offer.buyer_rs_percent ?? offer.rs_percent_default) || 0}%</span>
                      )}
                      <span className="rounded bg-[#22C55E]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#22C55E]">Your rate</span>
                    </div>
                  ) : (
                    <div className="font-mono text-base font-semibold text-[#22C55E]" data-testid="info-payout-value">
                      {offer.payment_model === "RevShare"
                        ? `${Number(isBuyer ? (offer.buyer_payout ?? offer.affiliate_payout) : offer.affiliate_payout) || 0}%`
                        : fmtMoney(isBuyer ? (offer.buyer_payout ?? offer.affiliate_payout) : offer.affiliate_payout, offer.currency)}
                      {offer.payment_model === "Hybrid" && (
                        <span className="text-[#F59E0B]" data-testid="info-payout-rs"> · RevShare {Number(isBuyer ? (offer.buyer_rs_percent ?? offer.rs_percent_default) : offer.rs_affiliate_percent) || 0}%</span>
                      )}
                    </div>
                  )}
                </div>
                )}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Model</div>
                  <div className="text-sm text-foreground">{offer.payment_model}</div>
                </div>
                {multiGeo && (
                  <div className="w-full" data-testid="info-geo-payouts">
                    <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground/60">Available GEOs</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {isBuyer
                        ? (offer.geo_payouts || []).map((r) => (
                          <span key={r.geo} className="flex items-center gap-1.5 text-xs" data-testid={`info-geo-payout-${r.geo}`}>
                            <span className="text-foreground">{r.geo === "*" ? "🌐 Other GEOs" : `${flagEmoji(r.geo)} ${geoName(r.geo)}`}</span>
                            {r.is_personal && <span className="font-mono text-muted-foreground/50 line-through">{r.percent ? `${r.payout_default}%` : fmtMoney(r.payout_default, offer.currency)}</span>}
                            <span className={cn("font-mono font-semibold", r.is_personal ? "text-[#22C55E]" : "text-[#22C55E]")}>{r.percent ? `${r.payout}%` : fmtMoney(r.payout, offer.currency)}</span>
                            {r.is_personal && <span className="rounded bg-[#22C55E]/15 px-1 py-px text-[9px] font-semibold text-[#22C55E]">Your rate</span>}
                          </span>
                        ))
                        : (
                          <>
                            {(offer.geos || []).map((g) => {
                              const e = (offer.geo_rates || {})[g] || {};
                              const pay = e.affiliate_payout ?? offer.affiliate_payout;
                              const pct = offer.payment_model === "RevShare";
                              return (
                                <span key={g} className="flex items-center gap-1.5 text-xs" data-testid={`info-geo-payout-${g}`}>
                                  <span className="text-foreground">{flagEmoji(g)} {geoName(g)}</span>
                                  <span className="font-mono font-semibold text-[#22C55E]">{pct ? `${Number(pay) || 0}%` : fmtMoney(pay, offer.currency)}</span>
                                </span>
                              );
                            })}
                            <span className="flex items-center gap-1.5 text-xs" data-testid="info-geo-payout-*">
                              <span className="text-foreground">🌐 Other GEOs</span>
                              <span className="font-mono font-semibold text-[#22C55E]">{offer.payment_model === "RevShare" ? `${Number(offer.affiliate_payout) || 0}%` : fmtMoney(offer.affiliate_payout, offer.currency)}</span>
                            </span>
                          </>
                        )}
                    </div>
                  </div>
                )}
                <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
                  {offer.min_dep ? <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {offer.min_dep} {offer.min_dep_currency || ""} min dep</span> : null}
                  <span className="inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> {offer.cap ? `${offer.cap} cap` : "no cap"}</span>
                  {/* Buyers never see the internal public/private/request wording. */}
                  {!isBuyer && <span className="inline-flex items-center gap-1">{offer.privacy === "private" ? <Lock className="h-3.5 w-3.5" /> : <Globe className="h-3.5 w-3.5" />} {offer.privacy}</span>}
                </div>
              </div>

              {description && (
                <div>
                  <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Description & instructions</h4>
                  <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{description}</p>
                </div>
              )}

              {canCreateStream && (
                <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.08] pt-4">
                  {/* Request-only offers: buyer must request access before streaming. */}
                  {access === "request_required" && (
                    <Button onClick={requestAccess} disabled={busy} data-testid={`info-request-access-${offer.id}`}
                      className="gap-1.5 bg-white text-black hover:bg-white/90"><Send className="h-4 w-4" /> Request access</Button>
                  )}
                  {access === "pending" && (
                    <div className="flex items-center gap-2 text-sm text-[#EAB308]" data-testid="info-request-pending">
                      <Hourglass className="h-4 w-4" /> Pending request — awaiting approval.
                    </div>
                  )}
                  {access === "rejected" && (
                    <div className="flex w-full flex-wrap items-center gap-2">
                      <span className="flex items-center gap-2 text-sm text-[#EF4444]" data-testid="info-request-rejected"><Ban className="h-4 w-4" /> Rejected. Contact your manager.</span>
                      <Button onClick={requestAccess} disabled={busy} variant="outline" data-testid={`info-request-again-${offer.id}`}
                        className="gap-1.5 border-white/[0.12] text-muted-foreground hover:text-foreground"><Send className="h-4 w-4" /> Request again</Button>
                    </div>
                  )}
                  {canStreamNow && offer.status === "active" && (
                    <Button onClick={() => setStream(true)} data-testid={`info-create-stream-${offer.id}`}
                      className="gap-1.5 bg-white text-black hover:bg-white/90"><Zap className="h-4 w-4" /> Create Stream</Button>
                  )}
                  {canStreamNow && (
                    <Button variant="outline" onClick={() => { onOpenChange(false); navigate("/streams"); }} data-testid="info-my-streams"
                      className="gap-1.5 border-white/[0.12] text-muted-foreground hover:text-foreground"><ListChecks className="h-4 w-4" /> My Streams</Button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <StreamDrawer open={stream} item={offer ? { offer_id: offer.id } : null} onOpenChange={setStream} />
    </>
  );
}
